import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { Button, CheckBox, CheckStyles, SECTION_BASIC, NewModal, MainContentPaddingTypes, NewInput, NewInputList, NewInputListHandles, Overlay, NewSelect } from "../..";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useParams } from "react-router";
import { SectionBasicTypes } from "../Section/sectionBasic";
import { ListStyle_Types, NewFieldStyles } from "../../../Definitions";
import { LfSectionsInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { useTerminals } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useTerminals";
import { isNumber } from "lodash";
import { useSelectOptions } from "../../_Components";

interface CalibrateSection_Props{
  isModalOpen: boolean
  onClose: () => void,
  system: number
}

export default function CalibrateSectionModal({isModalOpen, onClose, system}:CalibrateSection_Props) {
  const {t} = useTranslation()
  const inputRef = useRef<NewInputListHandles | null>(null)
  const [activePenId, setActivePenId] = useState<number[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [resolvedWeight, setResolvedWeight] = useState<number>(0);
  const [message, setMessage] = useState<string>("")
  const [isSucces, setIsSuccess] = useState<boolean>()
  const mobile = useMediaQuery(`(max-width: 650px)`);

  const {systemId: urlSystemId} = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : system;
  const {sectionOptions} = useSelectOptions({ systemId: systemId })

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const { updateTerminalConfigFromCalibration, setTerminalInCalibrationMode, doTerminalCalibration, data: terminals } = useTerminals(connection) 
  const { call: getLfSectionsInfo, data: lfSectionsState } = useSection(connection); 

  const sections = useMemo( () => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter(section => section.system_id === systemId) : undefined), [domain, lfSectionsState, systemId] );
  const [activeSection, setActiveSection] = useState<LfSectionsInfo | undefined>(sections !==undefined ? sections[0] : undefined);
  const numberOfPens = activeSection?.section_locations.length;
  
  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActiveSection(sections !==undefined ? sections[0] : undefined)
  },[sections])

  useEffect(() => {
    if (isModalOpen && activeSection) {
      setIsAllSelected(false);
    }
  }, [activeSection, isModalOpen]);
  
  useEffect(() => {
    setIsAllSelected(activePenId.length === numberOfPens);
  }, [activePenId, numberOfPens]);

  function handlePenSelected(penId: number) {
    setActivePenId((prev) => {
      if (!prev) return [penId]; 
      return prev.includes(penId)
        ? prev.filter(id => id !== penId) 
        : [...prev, penId]; 
    });
  }

  const penTerminals = terminals[domain]?.data.filter((terminal) => terminal.fk_locations ? activePenId.includes(terminal.fk_locations) : false )

  if (!sections) return null
  const currentIndex = sections.findIndex(s => s.section_id === activeSection?.section_id);

  const setCalibrationMode = () => {
    penTerminals.forEach(async (terminal) => {
      await setTerminalInCalibrationMode(terminal.terminal_id, true);
    })
    // setCalibrationOn(true) //TODO Only if setTerminalInCalibrationMode runs succesfully
  }

  const doCalibration = () => {
    penTerminals.forEach((terminal, index) => {
      const ref = inputRef.current;
      if (ref) {
        const config = {
          triggerAmount: Number(ref.refs.current[0].value),
          pauseTime: Number(ref.refs.current[1].value),
          calibrationAmount: Number(ref.refs.current[2].value)
        }
        doTerminalCalibration(terminal.terminal_id, config.triggerAmount, config.pauseTime, config.calibrationAmount)
      }
    })
  }

  const UpdateResolvedWeight = async () => {
    const ref = inputRef.current;
    if (ref) {
      const resWeight = Math.round(resolvedWeight / Number(ref.refs.current[2].value));
      if (isNumber(resWeight) && resWeight > 0 && activePenId.length > 0) {
        for (const terminal of penTerminals) {
          const success = await updateTerminalConfigFromCalibration(terminal.terminal_id, resWeight);
          if (success) {
            setMessage(t("success", {defaultValue: "Success"}));
            setIsSuccess(true)
          } else {
            setIsSuccess(false)
            setMessage(t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }))
          }
        }
      } else {
        setIsSuccess(false)
        setMessage(t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }))
      }
      setTimeout(() => {
        setIsSuccess(undefined)
        setMessage("");
      }, 5000);
    }
  };

  return (activeSection && (
    <NewModal
      header={t("systemControls.general.systemControls", {defaultValue: "System Controls"})}
      modalState={isModalOpen}
      predefinedButtons={{
        onClose: () => {setActivePenId([]); setActiveSection(sections[0]); onClose()},
        onNext: () => {setActivePenId([]); setActiveSection(sections[(currentIndex + 1) % sections.length])},
        onPrev: () => {setActivePenId([]); setActiveSection(sections[(currentIndex - 1 + sections.length) % sections.length])}
      }}
      subHeader={activeSection?.name
      }
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary
      }}
      headerContent={
        <NewSelect
          options={sectionOptions ?? []}
          key={activeSection.section_id}
          onChange={(i) => setActiveSection(sections.find(section => section.section_id === i?.id))}
          valueOverrides={{
            defaultValue: sectionOptions?.find(option => option.id === activeSection.section_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "100px" : "160px"
          }}
        />
      }
      mainContent={
        <div style={{ width: mobile ? "300px" : "600px", boxSizing: "border-box", gap: "20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : "270px 1fr", gridTemplateRows: mobile ? "fit-content(100%) fit-content(100%)" : "1fr", margin: "0 auto"}}>
          {/* Controls */}
          <div style={{ gridArea: mobile ? "2/1/3/2" : "1/1/2/2", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "15px"}}>
            <Button
              content={t("systemControls.general.calibrateSelected", {defaultValue: "Calibrate Selected"})}
              startEnhancer={glyphs.Start({size: "30px"})}
              overrides={{
                baseButton: {backgroundColor: colors.greySecondary},
                hoverStyles: {backgroundColor: colors.greyDarkSecondary},
                startEnhancerStyles: {...css.button_PositionStartEnhancerStart}
              }}
              onClick={() => {setCalibrationMode(); doCalibration()}}
            />
            {/* <div style={{height: "2px", width: "100%", backgroundColor: colors.greyTertiary}}/> */}
            <NewInputList
              ref={inputRef}
              headers={["Trigger Amount", "Pause Time", "Calibration Amount"]}
              placeholders={["", "", ""]}
              isDisabled={[true, true, true]}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greyTertiary
              }}
              listOverrides={{
                listStyle: ListStyle_Types.vertical
              }}
              valueOverrides={{
                defaultValues: [10, 2, 5]
              }}
            />
            {/* <div style={{height: "2px", width: "100%", backgroundColor: colors.greyTertiary}}/> */}
            <div>
              <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyPrimary, padding: "0px 0px 2px 10px"}}>{`${t("systemControls.general.resolvedWeight", {defaultValue: "Resolved Weight"})} (${t("units.gram", {defaultValue: "g"})})`}</p>
              <div style={{display: "flex", flexDirection: "row", alignItems: "end", justifyContent: "space-between", zIndex: "3", width: "280px"}}>
                <NewInput
                  placeholder="g"
                  fieldOverrides={{
                    fieldStyle: NewFieldStyles.greyTertiary,
                    fieldWidth: "137px"
                  }}
                  onChange={(e) => setResolvedWeight(Number(e))}
                  valueOverrides={{
                    valueType: "number"
                  }}
                />
                <Button
                  content={(t("buttons.commit", {defaultValue: "commit"}))}
                  onClick={() => UpdateResolvedWeight()} 
                  overrides={{
                    baseButton: {height: "30px", width: "137px", backgroundColor: colors.greySecondary},
                    hoverStyles: {backgroundColor: colors.greyDarkSecondary}
                  }}
                />
              </div>
            </div>

            <div style={{ height: "2px", width: "100%", backgroundColor: colors.greyTertiary }} />
            {/* Selected pens */}
            <div style={{ width: "100%", maxHeight: "200px", overflowX: "hidden", overflowY: "scroll", scrollbarWidth: "thin", scrollbarColor: colors.greyTertiary + colors.transparent, display: "flex", flexDirection: "column", gap: "5px"}}>
              {activePenId.length === 0 ? (
                <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, width: "100%", textAlign: "center"}}> {`${t("systemControls.general.noPensSelected", { defaultValue: "No pens selected" })}!`} </p>
              ) : (
                <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, width: "100%" }}> {`${t("systemControls.general.calibrating", {defaultValue: "Calibrating"})}:`} </p>
              )}
              <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>
                {activePenId
                  .map(penId => `${t("locationFeederPages.locationFeeder.pen", {defaultValue: "Pen"})} ${sections?.flatMap(section => section.section_locations).find(location => location.location_id === penId)?.name || penId}`)
                  .join(", ")}
              </p>
            </div>
            
            {/* Message */}
            <div style={{height: "30px", display: "flex", alignItems: "center", justifyContent: "center", width: "100%"}}>
              <p style={{...fonts.paragraph, ...fonts.fontSize4, color: isSucces ? colors.systemGreen : colors.systemRed}}>{message}</p>
            </div>

          </div>
          
          {/* Pens */}
          <div style={{ gridArea: mobile ? "1/1/2/2" : "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px", borderBottom: mobile ? "2px solid " + colors.greyTertiary : "none", borderLeft: mobile? "none" : "2px solid " + colors.greyTertiary, paddingBottom: mobile ? "15px" : "0px"}}>
            <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{`${t("systemControls.general.clickPen", {defaultValue: "Click every pen you want to select"})}:`}</p>
            <div key={activeSection?.section_id} style={{ 
              cursor: "pointer",
              transition: 'filter 0.3s',
              width: "fit-content",
              height: "fit-content",
              position: "relative"
            }}>
              <SECTION_BASIC 
                onPenClick={handlePenSelected}
                sectionId={activeSection?.section_id} 
                sectionType={SectionBasicTypes.selectablePen}
                pens={activeSection?.section_locations}
                selectedPenIds={activePenId}
              />
            </div>
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px"}}>
              <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{`${t("systemControls.general.orSelectAll", {defaultValue: "Or select all"})}:`}</p>
              <CheckBox
                checkStyle={CheckStyles.grey}
                onChange={() => {
                  if (isAllSelected) {
                    setActivePenId([]);
                  } else {
                    const allPenIds = activeSection?.section_locations.map(pen => pen.location_id);
                    setActivePenId(allPenIds);
                  }
                  setIsAllSelected(!isAllSelected);
                }} 
                isChecked={isAllSelected}
              />
            </div>
          </div>
        </div>
      }
    />
  ))
}